import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * Hotspot coordinates are percentages over the rose infographic
 * (top-left origin). Adjust if the source image is swapped.
 *
 * `cx,cy` = center of clickable region
 * `w,h`   = width / height of the region
 */
const hotspots = [
  {
    id: "flower",
    label: "Flower · Thriving Ward 3",
    eyebrow: "The Flower",
    title: "Our Vision for Prosperity",
    body:
      "Implementation of community-led safety initiatives and local business grants to ensure Ward 3 remains vibrant and safe.",
    cx: 50,
    cy: 8,
    w: 22,
    h: 14,
  },
  {
    id: "bud",
    label: "Bud · Future Growth",
    eyebrow: "The Bud",
    title: "Investing in the Next Generation",
    body:
      "Focus on youth mentorship, STEAM program funding, and creating pipelines for young leaders in our community.",
    cx: 23,
    cy: 18,
    w: 30,
    h: 14,
  },
  {
    id: "fruit",
    label: "Fruit · Generational Wealth",
    eyebrow: "The Fruit",
    title: "Building Lasting Legacies",
    body:
      "Expanding homeownership assistance and financial literacy programs to help Ward 3 families build and keep wealth.",
    cx: 80,
    cy: 32,
    w: 30,
    h: 16,
  },
  {
    id: "leaves",
    label: "Leaves · Community Support",
    eyebrow: "The Leaves",
    title: "The Heart of the Neighborhood",
    body:
      "Prioritizing health and wellness resources, senior care, and educational equity for every resident.",
    cx: 24,
    cy: 41,
    w: 32,
    h: 14,
  },
  {
    id: "thorns",
    label: "Thorns · Challenges We Overcome",
    eyebrow: "The Thorns",
    title: "Facing Obstacles Together",
    body:
      "Direct action on housing gaps, systemic inequality, and public safety through transparent legislation.",
    cx: 80,
    cy: 56,
    w: 30,
    h: 16,
  },
  {
    id: "stem",
    label: "Stem · Economic Mobility",
    eyebrow: "The Stem",
    title: "The Path to Success",
    body:
      "Workforce development partnerships and removing barriers to high-paying jobs for Ward 3 residents.",
    cx: 26,
    cy: 64,
    w: 36,
    h: 14,
  },
  {
    id: "roots",
    label: "Roots · Strong Foundations",
    eyebrow: "The Roots",
    title: "Stability First",
    body:
      "Protecting affordable housing and ensuring family stability as the bedrock of our ward.",
    cx: 32,
    cy: 89,
    w: 44,
    h: 11,
  },
];

const Platform = () => {
  const [active, setActive] = useState(null);
  const [grown, setGrown] = useState(false);
  const sectionRef = useRef(null);

  // Trigger grow animation once when the rose enters the viewport
  useEffect(() => {
    if (!sectionRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !grown) {
            setGrown(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, [grown]);

  const activeData = hotspots.find((h) => h.id === active);

  return (
    <section
      id="platform"
      data-testid="platform-section"
      className="relative bg-royal-deep py-24 lg:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="text-center mb-12 lg:mb-16 max-w-4xl mx-auto">
          <p className="text-[11px] tracking-[0.32em] uppercase text-lavender mb-5">
            03 — The Platform
          </p>
          <h2
            data-testid="platform-heading"
            className="font-serif-display text-classy text-5xl sm:text-6xl lg:text-7xl leading-[0.95] font-medium"
          >
            Together We Grow Ward 3
            <br />
            <span className="italic font-light">with Sikaiadah Newton</span>
          </h2>
          <div className="h-px w-24 bg-lavender/70 mx-auto my-7"></div>
          <p className="font-serif-display italic text-classy-soft text-xl sm:text-2xl lg:text-3xl">
            From Roots to Results
          </p>
          <p className="text-classy-soft/75 text-sm lg:text-base mt-6 max-w-2xl mx-auto leading-relaxed">
            Tap any part of the rose to explore Si's seven-pillar policy
            framework — every petal, leaf, and root grounded in real Ward 3
            priorities.
          </p>
        </div>

        {/* Rose infographic with hotspots */}
        <div
          ref={sectionRef}
          data-testid="rose-infographic"
          className="relative max-w-3xl mx-auto"
        >
          <div
            className={`relative w-full transition-[clip-path] duration-[1800ms] ease-[cubic-bezier(.2,.8,.2,1)] ${
              grown
                ? "[clip-path:inset(0_0_0_0)]"
                : "[clip-path:inset(100%_0_0_0)]"
            }`}
            style={{
              filter: "drop-shadow(0 30px 60px rgba(0,0,0,0.45))",
            }}
          >
            <img
              src="/assets/rose-cropped.jpg"
              alt="Together We Grow Ward 3 — From Roots to Results"
              className="w-full h-auto block rounded-sm"
            />

            {/* Hotspots */}
            {hotspots.map((h) => (
              <button
                key={h.id}
                data-testid={`rose-hotspot-${h.id}`}
                aria-label={h.label}
                onClick={() => setActive(h.id)}
                className="group absolute -translate-x-1/2 -translate-y-1/2 rounded-full focus:outline-none"
                style={{
                  left: `${h.cx}%`,
                  top: `${h.cy}%`,
                  width: `${h.w}%`,
                  height: `${h.h}%`,
                }}
              >
                {/* Subtle glow ring on hover/focus */}
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-[#2E1065]/0 group-hover:bg-[#C5B3E8]/15 group-focus-visible:bg-[#C5B3E8]/20 transition-colors duration-300 ring-0 group-hover:ring-2 group-focus-visible:ring-2 ring-[#C5B3E8]/70 ring-offset-0"
                ></span>
                {/* Pulse beacon */}
                <span
                  aria-hidden
                  className="absolute top-1.5 right-1.5 w-3 h-3 rounded-full bg-lavender shadow-[0_0_0_4px_rgba(197,179,232,0.25)]"
                >
                  <span className="absolute inset-0 rounded-full bg-lavender animate-ping opacity-70"></span>
                </span>
              </button>
            ))}
          </div>

          {/* Mobile-friendly tap legend below the rose */}
          <div className="lg:hidden mt-8 grid grid-cols-2 gap-2">
            {hotspots.map((h) => (
              <button
                key={`legend-${h.id}`}
                data-testid={`rose-legend-${h.id}`}
                onClick={() => setActive(h.id)}
                className="text-left text-[11px] uppercase tracking-[0.18em] text-classy-soft hover:text-lavender border border-white/15 hover:border-lavender/60 rounded-full px-4 py-2.5 transition-colors duration-300"
              >
                {h.label.split(" · ")[0]}
              </button>
            ))}
          </div>
        </div>

        {/* CTA below */}
        <div className="text-center mt-14 lg:mt-16">
          <p className="font-serif-display italic text-classy-soft text-lg lg:text-xl mb-6">
            "From the roots up — Ward 3, this is our growth plan."
          </p>
          <p className="text-classy-soft text-xs tracking-[0.2em] uppercase">
            — Sikaiadah Newton
          </p>
        </div>
      </div>

      {/* Modal */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="rose-modal"
          className="max-w-lg bg-royal-deep border border-lavender/30 text-classy"
        >
          {activeData && (
            <>
              <DialogHeader>
                <p
                  data-testid="rose-modal-eyebrow"
                  className="text-[10px] tracking-[0.32em] uppercase text-lavender mb-3"
                >
                  {activeData.eyebrow}
                </p>
                <DialogTitle
                  data-testid="rose-modal-title"
                  className="font-serif-display text-3xl lg:text-4xl text-classy leading-tight"
                >
                  {activeData.title}
                </DialogTitle>
                <DialogDescription
                  data-testid="rose-modal-body"
                  className="text-classy-soft/85 text-[15px] leading-relaxed pt-3"
                >
                  {activeData.body}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6">
                <a
                  href="#get-involved"
                  onClick={() => setActive(null)}
                  data-testid="rose-modal-cta"
                  className="btn-classy inline-flex w-full justify-center px-7 py-3.5 rounded-full text-xs uppercase"
                >
                  Join the Movement
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Platform;
export { Platform };
